import { Controller } from "@hotwired/stimulus"
import {
  MAX_EPISODE_BYTES,
  SUPPORTED_EPISODE_EXTENSIONS,
  canResumeEpisodeUpload,
  confirmedEpisodePartsMatch,
  episodeReviewUrl,
  episodeUploadProgress,
  pendingEpisodeParts,
  sha256Hex
} from "./episode_upload_helpers.mjs"

export default class extends Controller {
  static targets = ["form", "file", "startButton", "statusPanel", "status", "progress", "progressText", "pauseButton", "controls", "suppliedTranscript"]
  static values = {
    createUrl: String,
    baseUrl: String,
    reviewBaseUrl: String,
    existing: Object
  }

  connect() {
    this.paused = false
    this.abortController = null
    this.upload = this.hasExistingValue ? this.existingValue : null
    this.restoreVisibleState()
    this.toggleTranscriptInput()
  }

  toggleTranscriptInput() {
    if (!this.hasSuppliedTranscriptTarget) return

    const mode = new FormData(this.formTarget).get("transcript_mode")
    const supplied = mode === "supplied"
    this.suppliedTranscriptTarget.hidden = !supplied
    const textarea = this.suppliedTranscriptTarget.querySelector("textarea")
    if (textarea) textarea.required = supplied
  }

  disconnect() {
    this.abortController?.abort()
    window.clearTimeout(this.pollTimer)
  }

  async start(event) {
    event.preventDefault()
    const file = this.fileTarget.files?.[0]
    if (!this.validFile(file)) return

    this.paused = false
    this.startButtonTarget.disabled = true
    this.showStatus("Preparing your upload.")

    try {
      if (canResumeEpisodeUpload(this.upload, file)) {
        this.showStatus("Confirming the file before resume.")
        const confirmedPartsMatch = await confirmedEpisodePartsMatch(this.upload, file)
        if (!confirmedPartsMatch) throw new Error("Choose the original file, or discard this upload and start again.")
      } else {
        this.upload = await this.createUpload(file)
      }
      await this.uploadParts(file)
      if (this.paused) return

      this.showStatus("Checking the complete audio file.")
      this.upload = await this.requestJson(`${this.uploadUrl()}/complete`, { method: "POST" })
      this.renderProgress()
      this.pollUntilFinished()
    } catch (error) {
      if (error.name !== "AbortError") this.showStatus(error.message, true)
    } finally {
      this.startButtonTarget.disabled = false
    }
  }

  pause() {
    this.paused = true
    this.abortController?.abort()
    this.startButtonTarget.textContent = "Resume upload"
    this.showStatus("Upload paused. Choose the same file, then resume.")
  }

  async discard() {
    if (!this.upload?.id) return

    this.paused = true
    this.abortController?.abort()
    try {
      this.upload = await this.requestJson(this.uploadUrl(), { method: "DELETE" })
      this.showStatus("Incomplete upload discarded.")
      this.progressTarget.style.width = "0%"
      this.progressTextTarget.textContent = "0%"
      this.startButtonTarget.textContent = "Upload Episode"
    } catch (error) {
      this.showStatus(error.message, true)
    }
  }

  restoreVisibleState() {
    if (!this.upload) return

    this.renderProgress()
    if (["initialized", "uploading"].includes(this.upload.state)) {
      this.startButtonTarget.textContent = "Resume upload"
      this.showStatus(`Choose ${this.upload.filename} to resume confirmed parts.`)
    } else if (["completing", "validating"].includes(this.upload.state)) {
      this.formTarget.classList.add("hidden")
      this.showStatus("Checking the complete audio file.")
      this.pollUntilFinished()
    } else if (this.upload.state === "completed") {
      this.openReview()
    } else if (this.upload.state === "failed") {
      this.showStatus(this.upload.failure_message || "This audio file was not accepted.", true)
    } else if (this.upload.state === "discarding") {
      this.showStatus("Cleanup did not finish. Choose Discard upload to try again.", true)
    }
  }

  validFile(file) {
    if (!file) {
      this.showStatus("Choose an audio file.", true)
      return false
    }
    const extension = file.name.split(".").pop()?.toLowerCase()
    if (!SUPPORTED_EPISODE_EXTENSIONS.includes(extension)) {
      this.showStatus("Use MP3, M4A, AAC, WAV, WebM, OGG, or FLAC.", true)
      return false
    }
    if (file.size <= 0 || file.size > MAX_EPISODE_BYTES) {
      this.showStatus("Choose an audio file no larger than 500 MB.", true)
      return false
    }
    return true
  }

  async createUpload(file) {
    const form = new FormData(this.formTarget)
    return this.requestJson(this.createUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        episode_upload: {
          title: form.get("title"),
          description: form.get("description"),
          filename: file.name,
          byte_size: file.size,
          content_type: file.type,
          transcript_mode: form.get("transcript_mode"),
          supplied_transcript: form.get("transcript_mode") === "supplied" ? form.get("supplied_transcript") : null
        }
      })
    })
  }

  async uploadParts(file) {
    for (const partNumber of pendingEpisodeParts(this.upload)) {
      if (this.paused) return

      const start = (partNumber - 1) * Number(this.upload.part_size)
      const chunk = file.slice(start, Math.min(file.size, start + Number(this.upload.part_size)))
      const checksum = await sha256Hex(chunk)
      const body = new FormData()
      body.append("chunk", chunk, `part-${partNumber}.bin`)
      this.abortController = new AbortController()
      this.showStatus(`Uploading part ${partNumber} of ${this.upload.total_parts}.`)
      this.upload = await this.requestJson(`${this.uploadUrl()}/parts/${partNumber}`, {
        method: "PUT",
        headers: { "X-Part-SHA256": checksum },
        body,
        signal: this.abortController.signal
      })
      this.renderProgress()
    }
  }

  pollUntilFinished() {
    window.clearTimeout(this.pollTimer)
    this.pollTimer = window.setTimeout(async () => {
      try {
        this.upload = await this.requestJson(this.uploadUrl())
        this.renderProgress()
        if (this.upload.state === "completed") {
          this.openReview()
        } else if (this.upload.state === "failed") {
          this.formTarget.classList.remove("hidden")
          this.showStatus(this.upload.failure_message || "This audio file was not accepted.", true)
        } else {
          this.pollUntilFinished()
        }
      } catch (error) {
        this.showStatus(error.message, true)
      }
    }, 3000)
  }

  renderProgress() {
    const percentage = episodeUploadProgress(this.upload)
    this.statusPanelTarget.classList.remove("hidden")
    this.progressTarget.style.width = `${percentage}%`
    this.progressTarget.setAttribute("aria-valuenow", percentage.toString())
    this.progressTextTarget.textContent = `${percentage}%`
  }

  showStatus(message, error = false) {
    this.statusPanelTarget.classList.remove("hidden")
    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle("text-rose-700", error)
    this.statusTarget.setAttribute("role", error ? "alert" : "status")
    this.statusTarget.setAttribute("aria-live", error ? "assertive" : "polite")
    if (error) this.statusTarget.focus()
  }

  uploadUrl() {
    return `${this.baseUrlValue}/${this.upload.id}`
  }

  openReview() {
    const url = episodeReviewUrl(this.reviewBaseUrlValue, this.upload)
    if (url) {
      window.location.assign(url)
      return
    }

    this.formTarget.classList.add("hidden")
    this.controlsTarget.classList.add("hidden")
    this.showStatus("Your Draft Episode is ready.")
  }

  async requestJson(url, options = {}) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const headers = {
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      ...(options.headers || {})
    }
    const response = await fetch(url, { ...options, headers })
    const payload = await response.json()
    if (!response.ok || payload.success !== true) {
      throw new Error(payload.error?.message || "The upload could not continue.")
    }
    return payload.episode_upload
  }
}
